import React, { useState, useEffect } from "react";
import { Icon } from "../../icons.jsx";
import {
  Card, Button, Badge, Field, Check, Radio, Switch, Segmented, Modal,
  Donut, MultiDonut, BarChart, Slider,
} from "../components.jsx";
import { useStore } from "../store.jsx";
import { TALENTION_HELPERS } from "../data.js";

import { PageHead } from "./dashboard.jsx";
import { ImportarExcelModal } from "./importacion.jsx";
import { MetodoValoracionCard } from "./metodo-valoracion.jsx";
import { FactoresMinisterio } from "./ministerio-factores.jsx";

/* ============================================================
   Talention — Factores screen
   4 grupos, SVPT siempre = 100%, items con peso y selección
   ============================================================ */

const { FACTOR_STANDARDS, ITEM_TYPES, GENDERS, GENERIC_LEVELS, BINARY_LEVELS } = TALENTION_HELPERS;

/* Colores de la etiqueta de género. Son solo una ayuda visual: la etiqueta
   no interviene en el cálculo de puntos. */
const GENDER_TINT = {
  NEUTRO:    { bg: "#eef1f6", fg: "#5b6472", bar: "#cbd2dd" },
  MASCULINO: { bg: "#dfeeff", fg: "#0a52c2", bar: "#0a84ff" },
  FEMENINO:  { bg: "#fde2e8", fg: "#a01755", bar: "#ec4899" },
};

/* Un decimal y con coma: los pesos efectivos son cifras pequeñas y
   redondearlas a entero haría desaparecer subfactores del reparto. */
const fmtPct = (v) => (Math.round(v * 10) / 10).toString().replace(".", ",");

/* Un estándar aplicado deja de estar "tal cual" en cuanto alguien mueve un
   peso de grupo, así que se distingue aplicado de modificado. */
function standardState(standard, factors) {
  if (factors.standardId !== standard.id) return "off";
  const sameWeights = factors.groups.every(g => (standard.groups[g.id] ?? g.svpt) === g.svpt);
  return sameWeights ? "applied" : "modified";
}

/* Los ids de nivel viajan dentro de las respuestas ya guardadas, así que uno
   nuevo no puede reutilizar el id de ningún nivel que este subfactor haya
   tenido mientras el editor está abierto, ni siquiera el de uno recién
   borrado: la respuesta que apuntaba al viejo pasaría a significar el nuevo
   sin que pruneValuations lo detecte. */
function freshLevelIds(count, usedIds) {
  const fresh = [];
  let n = 0;
  while (fresh.length < count) {
    const id = "L" + n;
    if (!usedIds.has(id)) fresh.push(id);
    n++;
  }
  return fresh;
}

function FactorsTalention({ selectorMetodo }) {
  const { state, dispatch } = useStore();
  const [expanded, setExpanded] = useState(null);
  const [autoBalance, setAutoBalance] = useState(false);
  const [standardToApply, setStandardToApply] = useState(null);
  const [editingItem, setEditingItem] = useState(null);   // { groupId, itemId }
  const [importando, setImportando] = useState(false);

  const groups = state.factors.groups;
  const totalSVPT = groups.reduce((s, g) => s + g.svpt, 0);
  const balanced = totalSVPT === 100;
  const diff = totalSVPT - 100;

  const genderBalance = TALENTION_HELPERS.computeGenderBalance(state.factors);

  // El subfactor en edición se relee del estado en cada render: así el modal
  // no se queda con una copia vieja después de guardar.
  const editingGroup = editingItem ? groups.find(g => g.id === editingItem.groupId) : null;
  const editingTarget = editingItem
    ? (state.factors.items[editingItem.groupId] || []).find(it => it.id === editingItem.itemId)
    : null;

  const setSVPT = (groupId, value) => {
    if (autoBalance) {
      // adjust the other groups proportionally
      const remaining = 100 - value;
      const others = groups.filter(g => g.id !== groupId);
      const otherSum = others.reduce((s, g) => s + g.svpt, 0) || 1;
      others.forEach(g => {
        const ratio = g.svpt / otherSum;
        const newV = Math.round(remaining * ratio);
        dispatch({ type: "SET_FACTOR_SVPT", groupId: g.id, value: Math.max(0, newV) });
      });
      dispatch({ type: "SET_FACTOR_SVPT", groupId, value });
    } else {
      dispatch({ type: "SET_FACTOR_SVPT", groupId, value });
    }
  };

  return (
    <div className="stack" style={{ gap: 22 }}>
      <PageHead
        title="Factores de Valoración"
        sub="Define la importancia relativa de los cuatro grupos de factores. La suma de los pesos SVPT debe ser exactamente 100%."
        actions={
          <div className="row">
            <Button icon="template" onClick={() => setImportando(true)}>Importar factores</Button>
            <Button variant="ghost" icon="x" onClick={() => dispatch({ type: "RESET_FACTORS" })}>Reset</Button>
            <Button variant="secondary" onClick={() => dispatch({ type: "AUTOBALANCE_FACTORS" })} icon="sparkle">
              Reajustar a 100
            </Button>
          </div>
        }
      />

      {selectorMetodo}

      {/* Summary panel */}
      <Card style={{ overflow: "hidden", position: "relative" }}>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 28, alignItems: "center" }}>
          <MultiDonut
            size={180}
            stroke={20}
            segments={groups.map(g => ({ value: g.svpt, color: g.color }))}
          >
            <div>
              <div className="display-num" style={{ fontSize: 38, color: balanced ? "var(--ink-900)" : "var(--bad-500)" }}>
                {totalSVPT}<span style={{ fontSize: 22 }}>%</span>
              </div>
              <div className="tiny muted">{balanced ? "Equilibrado" : diff > 0 ? `${diff}% por encima` : `${-diff}% por debajo`}</div>
            </div>
          </MultiDonut>

          <div className="stack" style={{ gap: 10 }}>
            <div>
              <div className="card-eyebrow">Distribución actual de pesos</div>
              <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4, letterSpacing: "-0.02em" }}>
                {balanced ? "Pesos correctamente equilibrados al 100%" : "Ajusta los pesos para sumar 100%"}
              </div>
            </div>
            <div className="grid grid-4" style={{ gap: 10 }}>
              {groups.map(g => (
                <div key={g.id} style={{
                  padding: "10px 12px", background: "var(--bg-soft)", borderRadius: 10,
                  borderLeft: `3px solid ${g.color}`,
                }}>
                  <div className="tiny muted">{g.short}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {g.svpt}<span className="muted" style={{ fontSize: 13, fontWeight: 500 }}>%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="stack" style={{ gap: 8, alignItems: "stretch", minWidth: 160 }}>
            <div className="card-eyebrow">Auto-balance</div>
            <div className="row" style={{ gap: 8 }}>
              <Switch on={autoBalance} onChange={setAutoBalance} />
              <span className="tiny muted">Reajustar al mover</span>
            </div>
          </div>
        </div>

        {!balanced && (
          <div className={"banner " + (Math.abs(diff) > 15 ? "bad" : "warn")} style={{ marginTop: 18 }}>
            <Icon name="alert" size={18} className="banner-icon" />
            <div style={{ flex: 1 }}>
              <div className="semi" style={{ fontSize: 13 }}>
                {diff > 0
                  ? `Los pesos suman ${totalSVPT}%, te has pasado ${diff} puntos`
                  : `Los pesos suman ${totalSVPT}%, faltan ${-diff} puntos para llegar al 100%`}
              </div>
              <div className="tiny" style={{ marginTop: 2, opacity: 0.85 }}>
                Las valoraciones de los puestos no son fiables hasta que los pesos sumen exactamente 100%.
              </div>
            </div>
            <button onClick={() => dispatch({ type: "AUTOBALANCE_FACTORS" })}
              style={{
                border: "none", background: "white", padding: "7px 12px",
                borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>
              Reajustar automáticamente
            </button>
          </div>
        )}
      </Card>

      {/* Estándares predefinidos */}
      <Card
        eyebrow="Configuración de partida"
        title="Estándares predefinidos"
        action={<Badge>{FACTOR_STANDARDS.length} estándares</Badge>}
      >
        <div className="tiny muted" style={{ marginTop: -6, marginBottom: 14, lineHeight: 1.5, textWrap: "pretty" }}>
          Aplicar un estándar deja los pesos de grupo, la selección de subfactores y su ponderación tal y como los define
          ese estándar. Sustituye por completo lo que haya configurado ahora mismo.
        </div>
        <div className="grid grid-2">
          {FACTOR_STANDARDS.map(std => (
            <StandardCard
              key={std.id}
              standard={std}
              groups={groups}
              status={standardState(std, state.factors)}
              onApply={() => setStandardToApply(std)}
            />
          ))}
        </div>
      </Card>

      {/* 4 factor cards */}
      <div className="grid grid-2">
        {groups.map(group => (
          <FactorCard
            key={group.id}
            group={group}
            items={state.factors.items[group.id]}
            expanded={expanded === group.id}
            onToggle={() => setExpanded(expanded === group.id ? null : group.id)}
            onSVPT={(v) => setSVPT(group.id, v)}
            onToggleItem={(itemId) => dispatch({ type: "TOGGLE_FACTOR_ITEM", groupId: group.id, itemId })}
            onWeight={(itemId, value) => dispatch({ type: "SET_ITEM_WEIGHT", groupId: group.id, itemId, value })}
            onEqualize={() => dispatch({ type: "EQUALIZE_ITEM_WEIGHTS", groupId: group.id })}
            onEditItem={(itemId) => setEditingItem({ groupId: group.id, itemId })}
          />
        ))}
      </div>

      {/* Reparto por etiqueta de género */}
      <GenderBalanceCard
        balance={genderBalance}
        onEditItem={(groupId, itemId) => setEditingItem({ groupId, itemId })}
      />

      <div className="banner info">
        <Icon name="info" size={18} className="banner-icon" />
        <div>
          <div className="semi" style={{ fontSize: 13, marginBottom: 2 }}>¿Cómo funcionan los pesos?</div>
          <div className="tiny" style={{ lineHeight: 1.5 }}>
            El <b>SVPT (Sub-Valor Ponderado Total)</b> es el peso relativo de cada grupo en el cálculo final. Dentro de cada grupo, los <b>pesos individuales</b> de los factores seleccionados se reparten proporcionalmente. Los grupos suman 100% entre todos.
          </div>
        </div>
      </div>

      <StandardConfirmModal
        open={standardToApply !== null}
        standard={standardToApply}
        factors={state.factors}
        valuations={state.valuations}
        onClose={() => setStandardToApply(null)}
        onConfirm={() => {
          dispatch({ type: "APPLY_FACTOR_STANDARD", standard: standardToApply });
          setStandardToApply(null);
        }}
      />

      <FactorItemModal
        open={editingTarget != null}
        item={editingTarget}
        groupName={editingGroup ? editingGroup.name : ""}
        valuations={state.valuations}
        onClose={() => setEditingItem(null)}
        onSave={(patch) => {
          if (Object.keys(patch).length > 0) {
            dispatch({ type: "UPDATE_FACTOR_ITEM", groupId: editingItem.groupId, itemId: editingItem.itemId, patch });
          }
          setEditingItem(null);
        }}
        onDelete={() => {
          dispatch({ type: "DELETE_FACTOR_ITEM", groupId: editingItem.groupId, itemId: editingItem.itemId });
          setEditingItem(null);
        }}
      />

      <ImportarExcelModal open={importando} tipo="factores" onClose={() => setImportando(false)} />
    </div>
  );
}

/* ---- Estándares ---- */

function StandardCard({ standard, groups, status, onApply }) {
  const applied = status === "applied";
  const modified = status === "modified";
  const itemCount = Object.values(standard.items).reduce((s, arr) => s + arr.length, 0);

  return (
    <div className="surface" style={{
      padding: 0, overflow: "hidden",
      background: "var(--bg-card)",
      borderColor: applied ? "var(--good-500)" : "var(--border)",
    }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
        <div className="spread">
          <div className="row" style={{ gap: 10, minWidth: 0 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: "var(--ink-900)", color: "white",
              display: "grid", placeItems: "center", flexShrink: 0,
            }}>
              <Icon name="template" size={15} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="semi" style={{ fontSize: 14, letterSpacing: "-0.015em" }}>{standard.name}</div>
              <div className="tiny muted">{standard.fullName}</div>
            </div>
          </div>
          {applied && <Badge tone="good" dot>Aplicado</Badge>}
          {modified && <Badge tone="warn" dot>Modificado</Badge>}
        </div>
        <div className="tiny muted" style={{ marginTop: 10, lineHeight: 1.5, textWrap: "pretty" }}>
          {standard.description}
        </div>
      </div>

      <div style={{ padding: "14px 16px" }}>
        <div className="card-eyebrow" style={{ marginBottom: 8 }}>Pesos de grupo del estándar</div>
        <div className="grid grid-4" style={{ gap: 8 }}>
          {groups.map(g => (
            <div key={g.id} style={{
              padding: "8px 10px", background: "var(--bg-soft)", borderRadius: 9,
              borderLeft: `3px solid ${g.color}`,
            }}>
              <div className="tiny muted">{g.short}</div>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em" }}>
                {standard.groups[g.id] ?? 0}<span className="muted" style={{ fontSize: 11, fontWeight: 500 }}>%</span>
              </div>
            </div>
          ))}
        </div>

        {standard.provisional && (
          <div className="banner warn" style={{ marginTop: 12 }}>
            <Icon name="alert" size={16} className="banner-icon" />
            <div style={{ flex: 1 }}>
              <div className="semi" style={{ fontSize: 12.5 }}>Pesos provisionales, no oficiales</div>
              <div className="tiny" style={{ marginTop: 2, lineHeight: 1.5 }}>
                Los pesos y los subfactores de este estándar están puestos para que el mecanismo funcione de punta a punta.
                Hay que sustituirlos por los valores oficiales antes de usarlos con un cliente.
              </div>
            </div>
          </div>
        )}

        {modified && (
          <div className="tiny muted" style={{ marginTop: 12, lineHeight: 1.5 }}>
            Este estándar se aplicó, pero los pesos de grupo actuales ya no coinciden con los suyos.
          </div>
        )}

        <div className="spread" style={{ marginTop: 14 }}>
          <span className="tiny muted">{itemCount} subfactores seleccionados</span>
          <Button variant={applied ? "secondary" : "primary"} icon="swap" onClick={onApply}>
            {applied || modified ? "Volver a aplicar" : "Aplicar"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StandardConfirmModal({ open, standard, factors, valuations, onClose, onConfirm }) {
  if (!open || !standard) return null;

  // Se previsualiza el resultado con los mismos helpers que usa el store, así
  // el aviso de respuestas perdidas es el número real y no una estimación.
  const nextFactors = TALENTION_HELPERS.applyStandardToFactors(factors, standard);
  const { valuations: nextValuations, removed } = TALENTION_HELPERS.pruneValuations(valuations, nextFactors);

  // Un estándar puede no borrar ni una respuesta y aun así descolocar el
  // trabajo hecho: si añade subfactores seleccionados sin responder, las
  // valoraciones finalizadas vuelven a "en progreso". Se avisa siempre.
  const isComplete = (counts) => counts.total > 0 && counts.done === counts.total;
  const brokenCompletions = Object.keys(valuations).filter(positionId => {
    if (!isComplete(TALENTION_HELPERS.countCompletedItems(valuations[positionId], factors))) return false;
    return !isComplete(TALENTION_HELPERS.countCompletedItems(nextValuations[positionId], nextFactors));
  }).length;

  const countSelected = (f) => f.groups.reduce(
    (s, g) => s + (f.items[g.id] || []).filter(it => it.selected).length, 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Aplicar ${standard.name}`}
      width={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" icon="swap" onClick={onConfirm}>Aplicar estándar</Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="tiny muted" style={{ lineHeight: 1.5 }}>{standard.fullName} · {standard.description}</div>

        {standard.provisional && (
          <div className="banner bad">
            <Icon name="alert" size={18} className="banner-icon" />
            <div style={{ flex: 1 }}>
              <div className="semi" style={{ fontSize: 13 }}>Atención: los pesos de este estándar son PROVISIONALES</div>
              <div className="tiny" style={{ marginTop: 3, lineHeight: 1.5 }}>
                Están puestos para que el mecanismo de aplicación funcione, no son los valores oficiales del estándar.
                Antes de usar esta configuración con un cliente hay que sustituirlos por los pesos y subfactores reales.
              </div>
            </div>
          </div>
        )}

        <div className="banner warn">
          <Icon name="alert" size={18} className="banner-icon" />
          <div style={{ flex: 1 }}>
            <div className="semi" style={{ fontSize: 13 }}>Esto sustituye la configuración actual</div>
            <div className="tiny" style={{ marginTop: 3, lineHeight: 1.5 }}>
              Se reemplazan <b>los pesos de los cuatro grupos</b>, <b>qué subfactores están seleccionados</b> y
              <b> la ponderación de cada uno</b>. Los subfactores que no estén en el estándar quedan deseleccionados
              con peso 0. No se puede deshacer.
            </div>
          </div>
        </div>

        <div>
          <div className="card-eyebrow" style={{ marginBottom: 8 }}>Pesos de grupo</div>
          <table className="t-table">
            <thead><tr>
              <th>Grupo</th>
              <th style={{ width: 90 }}>Ahora</th>
              <th style={{ width: 90 }}>Estándar</th>
            </tr></thead>
            <tbody>
              {factors.groups.map(g => {
                const next = standard.groups[g.id] ?? g.svpt;
                return (
                  <tr key={g.id}>
                    <td>
                      <div className="row" style={{ gap: 8 }}>
                        <span className="swatch" style={{ background: g.color }} />
                        <b>{g.name}</b>
                      </div>
                    </td>
                    <td className="mono muted">{g.svpt}%</td>
                    <td>
                      <span className="mono semi" style={{ color: next === g.svpt ? "var(--ink-500)" : g.color }}>
                        {next}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="surface" style={{ display: "flex", gap: 20 }}>
          <div>
            <div className="card-eyebrow">Subfactores seleccionados</div>
            <div className="semi" style={{ fontSize: 14, marginTop: 3 }}>
              {countSelected(factors)} <span className="muted">→</span> {countSelected(nextFactors)}
            </div>
          </div>
          <div>
            <div className="card-eyebrow">Suma de pesos de grupo</div>
            <div className="semi" style={{ fontSize: 14, marginTop: 3 }}>
              {nextFactors.groups.reduce((s, g) => s + g.svpt, 0)}%
            </div>
          </div>
        </div>

        {removed > 0 && (
          <div className="banner bad">
            <Icon name="alert" size={18} className="banner-icon" />
            <div className="tiny" style={{ flex: 1, lineHeight: 1.5 }}>
              {removed === 1 ? (
                <>
                  Se borrará <b>1 respuesta</b> ya dada, porque apunta a un nivel que este estándar cambia o a un
                  subfactor que deja fuera.
                </>
              ) : (
                <>
                  Se borrarán <b>{removed} respuestas</b> ya dadas, porque apuntan a niveles que este estándar cambia
                  o a subfactores que deja fuera.
                </>
              )}
            </div>
          </div>
        )}

        {brokenCompletions > 0 && (
          <div className="banner warn">
            <Icon name="alert" size={18} className="banner-icon" />
            <div className="tiny" style={{ flex: 1, lineHeight: 1.5 }}>
              {brokenCompletions === 1 ? (
                <>
                  <b>1 puesto</b> que ahora está finalizado dejará de estarlo: con esta configuración le quedan
                  subfactores sin responder y habrá que volver a valorarlo.
                </>
              ) : (
                <>
                  <b>{brokenCompletions} puestos</b> que ahora están finalizados dejarán de estarlo: con esta
                  configuración les quedan subfactores sin responder y habrá que volver a valorarlos.
                </>
              )}
            </div>
          </div>
        )}

        {removed === 0 && brokenCompletions === 0 && (
          <div className="banner info">
            <Icon name="info" size={18} className="banner-icon" />
            <div className="tiny" style={{ flex: 1, lineHeight: 1.5 }}>
              Ninguna respuesta ya dada se pierde con este estándar y ningún puesto finalizado deja de estarlo, pero
              los puntos de los puestos se recalculan con los pesos nuevos.
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---- Grupos y subfactores ---- */

function FactorCard({ group, items, expanded, onToggle, onSVPT, onToggleItem, onWeight, onEqualize, onEditItem }) {
  const selectedCount = items.filter(i => i.selected).length;
  const itemWeightSum = items.filter(i => i.selected && i.levels.length > 0).reduce((s, i) => s + (i.weight || 0), 0);

  return (
    <Card className={"factor-card " + group.tint} style={{ padding: 0, overflow: "hidden", "--tint": group.color }}>
      <div className="factor-accent" style={{ background: group.color }} />
      <div style={{ padding: 22 }}>
        <div className="row" style={{ gap: 12, marginBottom: 16 }}>
          <div className="factor-emoji" style={{ background: group.iconBg, color: group.color }}>
            <Icon name={group.icon} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.015em" }}>{group.name}</div>
            <div className="tiny muted" style={{ marginTop: 2, textWrap: "pretty" }}>{group.description}</div>
          </div>
        </div>

        {/* SVPT slider */}
        <div style={{
          padding: 16, borderRadius: 14, background: "var(--bg-soft)",
        }}>
          <div className="spread" style={{ marginBottom: 8 }}>
            <span className="card-eyebrow">SVPT</span>
            <span style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" }}>
              {group.svpt}<span className="muted" style={{ fontSize: 14, fontWeight: 500 }}>%</span>
            </span>
          </div>
          <Slider value={group.svpt} onChange={onSVPT} min={0} max={100} color={group.color} />
        </div>

        {/* Item summary */}
        <div className="spread" style={{ marginTop: 16 }}>
          <div className="row" style={{ gap: 8 }}>
            <Badge style={{ background: group.color + "1f", color: group.color }}>{selectedCount} de {items.length} seleccionados</Badge>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {selectedCount > 0 && (
              <button onClick={onEqualize} className="btn btn-ghost btn-sm" style={{ gap: 4 }}>
                <Icon name="sparkle" size={12} />
                Igualar
              </button>
            )}
            <button onClick={onToggle} className="btn btn-ghost btn-sm" style={{ gap: 4 }}>
              <Icon name={expanded ? "chevron-down" : "chevron-right"} size={14} />
              {expanded ? "Ocultar" : "Ver factores"}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="fade-in" style={{ borderTop: "1px solid var(--border)", background: "var(--bg-soft)" }}>
          <div style={{ padding: "8px 0" }}>
            {items.map(it => (
              <FactorItemRow
                key={it.id}
                item={it}
                tint={group.color}
                groupSvpt={group.svpt}
                itemWeightSum={itemWeightSum}
                onToggle={() => onToggleItem(it.id)}
                onWeight={(v) => onWeight(it.id, v)}
                onEdit={() => onEditItem(it.id)}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function GenderChip({ gender }) {
  if (!gender || gender === "NEUTRO") return null;
  const tint = GENDER_TINT[gender] || GENDER_TINT.NEUTRO;
  return (
    <span
      title="Etiqueta informativa: no cambia la puntuación del puesto"
      style={{
        fontSize: 9.5, padding: "1px 6px", borderRadius: 999, fontWeight: 700,
        letterSpacing: "0.04em", whiteSpace: "nowrap",
        background: tint.bg, color: tint.fg,
      }}>{gender}</span>
  );
}

function FactorItemRow({ item, tint, groupSvpt, itemWeightSum, onToggle, onWeight, onEdit }) {
  const w = item.weight || 0;
  const effectivePct = itemWeightSum > 0 ? Math.round((w / itemWeightSum) * groupSvpt) : 0;
  const noLevels = !item.levels || item.levels.length === 0;

  return (
    <div style={{
      padding: "10px 22px",
      borderBottom: "1px solid var(--border)",
      background: item.selected ? "white" : "transparent",
      transition: "background 120ms",
    }}>
      <div className="row" style={{ gap: 14 }}>
        <Check checked={item.selected} onChange={onToggle} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="semi" style={{ fontSize: 13.5, color: item.selected ? "var(--ink-900)" : "var(--ink-500)" }}>{item.name}</span>
            <GenderChip gender={item.gender} />
            {item.personalizado && <Badge>Personalizado</Badge>}
            {item.type === "binario" && <span className="tiny muted">· Sí/No</span>}
            {noLevels
              ? <Badge tone="warn">Sin niveles</Badge>
              : <span className="tiny muted">· {item.levels.length} niveles</span>}
          </div>
          {noLevels ? (
            <div className="tiny" style={{ marginTop: 2, color: "#92400e" }}>
              No se puede valorar hasta que le definas niveles de respuesta: edítalo para dárselos.
            </div>
          ) : (
            item.desc && item.selected && <div className="tiny muted" style={{ marginTop: 2 }}>{item.desc}</div>
          )}
        </div>
        {item.selected && !noLevels && (
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <button
              onClick={() => onWeight(Math.max(0, w - 1))}
              style={{
                width: 26, height: 26, border: "1px solid var(--border)", borderRadius: 6,
                background: "var(--bg-soft)", cursor: "pointer", fontWeight: 700, fontSize: 15,
                display: "grid", placeItems: "center", lineHeight: 1,
              }}>−</button>
            <input
              type="number"
              min={0}
              value={w}
              onChange={e => onWeight(Math.max(0, parseInt(e.target.value) || 0))}
              style={{
                width: 52, textAlign: "center", border: "1px solid var(--border)", borderRadius: 6,
                padding: "4px 4px", fontSize: 13, fontWeight: 600, outline: "none",
                background: "white",
              }}
            />
            <button
              onClick={() => onWeight(w + 1)}
              style={{
                width: 26, height: 26, border: "1px solid var(--border)", borderRadius: 6,
                background: "var(--bg-soft)", cursor: "pointer", fontWeight: 700, fontSize: 15,
                display: "grid", placeItems: "center", lineHeight: 1,
              }}>+</button>
            <span style={{
              fontSize: 11.5, fontWeight: 600, minWidth: 38, textAlign: "right",
              color: effectivePct > 0 ? tint : "var(--ink-400)",
            }}>{effectivePct}%</span>
          </div>
        )}
        <button className="icon-btn" title="Editar subfactor" aria-label={`Editar ${item.name}`} onClick={onEdit} style={{ width: 28, height: 28, flexShrink: 0 }}>
          <Icon name="edit" size={13} />
        </button>
      </div>
    </div>
  );
}

/* ---- Editor de subfactor ---- */

function FactorItemModal({ open, item, groupName, valuations, onClose, onSave, onDelete }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [type, setType] = useState("escala");
  const [gender, setGender] = useState("NEUTRO");
  const [weight, setWeight] = useState(0);
  const [levels, setLevels] = useState([]);
  // Niveles que había antes de que un cambio de tipo propusiera otros: sin
  // esto, cambiar de tipo por error se llevaría por delante el trabajo hecho.
  const [replaced, setReplaced] = useState(null);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState(false);
  // Todos los ids que el subfactor ha llegado a tener en esta edición. Al
  // reabrir el modal se parte de los niveles guardados: para entonces el
  // store ya ha borrado las respuestas que apuntaban a los ids desechados.
  const [usedLevelIds, setUsedLevelIds] = useState(() => new Set());

  useEffect(() => {
    if (!item) return;
    setName(item.name || "");
    setDesc(item.desc || "");
    setType(item.type || "escala");
    setGender(item.gender || "NEUTRO");
    setWeight(item.weight || 0);
    setLevels((item.levels || []).map(l => ({ ...l })));
    setUsedLevelIds(new Set((item.levels || []).map(l => l.id)));
    setReplaced(null);
    setConfirmandoBorrado(false);
  }, [open, item]);

  if (!open || !item) return null;

  const respuestasDelFactor = Object.values(valuations || {}).filter(v => v && v[item.id] != null).length;

  // Los niveles propuestos al cambiar de tipo estrenan ids: si copiaran los
  // de la plantilla, las respuestas ya dadas se empalmarían con niveles que
  // significan otra cosa en vez de darse por perdidas.
  const proposeLevels = (template) => {
    const ids = freshLevelIds(template.length, usedLevelIds);
    const merged = new Set(usedLevelIds);
    ids.forEach(id => merged.add(id));
    setUsedLevelIds(merged);
    setReplaced(levels);
    setLevels(template.map((l, i) => ({ ...l, id: ids[i] })));
  };

  const changeType = (next) => {
    if (next === type) return;
    setType(next);
    if (next === "binario" && levels.length !== 2) {
      proposeLevels(BINARY_LEVELS);
    } else if (next === "escala" && levels.length <= 2) {
      proposeLevels(GENERIC_LEVELS);
    }
  };

  const setLevelField = (idx, field, value) =>
    setLevels(levels.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));

  const moveLevel = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= levels.length) return;
    const next = [...levels];
    next[idx] = levels[target];
    next[target] = levels[idx];
    setLevels(next);
  };

  const removeLevel = (idx) => setLevels(levels.filter((_, i) => i !== idx));

  const addLevel = () => {
    const [id] = freshLevelIds(1, usedLevelIds);
    setUsedLevelIds(new Set(usedLevelIds).add(id));
    setLevels([...levels, { id, label: "", desc: "" }]);
  };

  const undoReplace = () => {
    setLevels(replaced);
    setReplaced(null);
  };

  // Respuestas que quedarían huérfanas con los niveles tal y como están ahora.
  const lostAnswers = Object.values(valuations)
    .filter(answers => answers[item.id] && !levels.some(l => l.id === answers[item.id]))
    .length;

  const trimmedName = name.trim();
  const blocking =
    !trimmedName ? "El subfactor necesita un nombre."
    : levels.length === 1 ? "Con un solo nivel el subfactor no puede puntuar: añade otro nivel o bórralos todos."
    : levels.some(l => !(l.label || "").trim()) ? "Todos los niveles necesitan una etiqueta."
    : null;

  const save = () => {
    if (blocking) return;
    const patch = {};
    if (trimmedName !== item.name) patch.name = trimmedName;
    if (desc !== (item.desc || "")) patch.desc = desc;
    if (type !== item.type) patch.type = type;
    if (gender !== item.gender) patch.gender = gender;
    if ((Number(weight) || 0) !== item.weight) patch.weight = Number(weight) || 0;
    if (JSON.stringify(levels) !== JSON.stringify(item.levels || [])) patch.levels = levels;
    onSave(patch);
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title="Editar subfactor"
      width={680}
      footer={
        <>
          {item.personalizado && (
            <Button variant="ghost" icon="trash" onClick={() => setConfirmandoBorrado(true)} style={{ marginRight: "auto", color: "var(--danger-700)" }}>
              Eliminar factor
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <button
            className="btn btn-primary"
            disabled={!!blocking}
            onClick={save}
            style={blocking ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <Icon name="check" size={15} />
            Guardar cambios
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        <div className="row" style={{ gap: 8 }}>
          <Badge>{groupName}</Badge>
          <span className="tiny muted mono">{item.id}</span>
        </div>

        <Field label="Nombre">
          <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Ej. Formación académica" />
        </Field>

        <Field label="Descripción" hint="Se muestra al valorar el puesto, debajo del nombre del subfactor.">
          <textarea className="textarea" style={{ minHeight: 60 }} value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="Qué mide este subfactor" />
        </Field>

        <div className="grid grid-2">
          <Field label="Tipo" hint={(ITEM_TYPES.find(t => t.id === type) || {}).desc}>
            <Segmented
              value={type}
              options={ITEM_TYPES.map(t => ({ value: t.id, label: t.name }))}
              onChange={changeType}
            />
          </Field>
          <Field label="Ponderación dentro del grupo" hint="Se reparte proporcionalmente con el resto de subfactores seleccionados.">
            <input type="number" min={0} className="input" value={weight}
              onChange={e => setWeight(Math.max(0, parseInt(e.target.value) || 0))} />
          </Field>
        </div>

        <Field
          label="Etiqueta de género"
          hint="Informativa: no interviene en el cálculo de puntos. Solo sirve para detectar si la valoración se apoya demasiado en factores asociados históricamente a un género. Vuelve a NEUTRO para quitarla."
        >
          <Segmented
            value={gender}
            options={GENDERS.map(g => ({ value: g.id, label: g.name }))}
            onChange={setGender}
          />
        </Field>

        {replaced && (
          <div className="banner warn">
            <Icon name="alert" size={16} className="banner-icon" />
            <div style={{ flex: 1 }}>
              <div className="semi" style={{ fontSize: 12.5 }}>Se han propuesto niveles nuevos para este tipo</div>
              <div className="tiny" style={{ marginTop: 2, lineHeight: 1.5 }}>
                {replaced.length === 1
                  ? "El nivel que había sigue sin guardarse: puedes editar el propuesto o recuperar el anterior."
                  : `Los ${replaced.length} niveles que había siguen sin guardarse: puedes editar los propuestos o recuperar los anteriores.`}
              </div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={undoReplace}>Recuperar los anteriores</button>
          </div>
        )}

        {/* Editor de niveles */}
        <div>
          <div className="spread" style={{ marginBottom: 4 }}>
            <span className="field-label">Niveles de respuesta</span>
            <button className="btn btn-ghost btn-sm" onClick={addLevel} style={{ gap: 4 }}>
              <Icon name="plus" size={12} />
              Añadir nivel
            </button>
          </div>
          <div className="tiny muted" style={{ marginBottom: 8, lineHeight: 1.5 }}>
            <b>El orden importa</b>: el primero es el mínimo y puntúa 0, el último es el máximo y puntúa el peso completo
            del subfactor. Los intermedios se reparten en partes iguales entre ambos.
          </div>

          {/* la lista tiene scroll propio: el modal compartido no lo tiene y
              un subfactor de siete niveles se saldría de la ventana */}
          {levels.length === 0 ? (
            <div className="empty" style={{ padding: "22px 16px" }}>
              Sin niveles: este subfactor no se puede valorar todavía. Añade al menos dos.
            </div>
          ) : (
            <div className="stack" style={{ gap: 6, maxHeight: 320, overflowY: "auto" }}>
              {levels.map((l, i) => (
                <div key={l.id} className="surface" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <div style={{ width: 62, flexShrink: 0 }}>
                    <div className="mono semi" style={{ fontSize: 12 }}>{l.id}</div>
                    <div className="tiny muted">
                      {i === 0 ? "mínimo" : i === levels.length - 1 ? "máximo" : "nivel " + i}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 0 }}>
                    <input className="input" style={{ flex: "0 0 38%" }} value={l.label || ""} placeholder="Etiqueta (ej. Medio)"
                      onChange={e => setLevelField(i, "label", e.target.value)} />
                    <input className="input" style={{ flex: 1, fontSize: 12.5 }} value={l.desc || ""} placeholder="Descripción (opcional)"
                      onChange={e => setLevelField(i, "desc", e.target.value)} />
                  </div>
                  <div className="row" style={{ gap: 4, flexShrink: 0 }}>
                    <button className="icon-btn" title="Subir" onClick={() => moveLevel(i, -1)}
                      style={{ width: 28, height: 28, opacity: i === 0 ? 0.35 : 1 }}>
                      <Icon name="arrow-up" size={13} />
                    </button>
                    <button className="icon-btn" title="Bajar" onClick={() => moveLevel(i, 1)}
                      style={{ width: 28, height: 28, opacity: i === levels.length - 1 ? 0.35 : 1 }}>
                      <Icon name="arrow-down" size={13} />
                    </button>
                    <button className="icon-btn" title="Eliminar nivel" onClick={() => removeLevel(i)}
                      style={{ width: 28, height: 28 }}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {lostAnswers > 0 ? (
          <div className="banner bad">
            <Icon name="alert" size={18} className="banner-icon" />
            <div className="tiny" style={{ flex: 1, lineHeight: 1.5 }}>
              {lostAnswers === 1 ? (
                <>
                  Al guardar se perderá <b>1 respuesta</b> ya dada a este subfactor, porque apunta a un nivel que ya no
                  existe. Ese puesto volverá a quedar incompleto.
                </>
              ) : (
                <>
                  Al guardar se perderán <b>{lostAnswers} respuestas</b> ya dadas a este subfactor, porque apuntan a
                  niveles que ya no existen. Esos puestos volverán a quedar incompletos.
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="banner info">
            <Icon name="info" size={18} className="banner-icon" />
            <div className="tiny" style={{ flex: 1, lineHeight: 1.5 }}>
              Las respuestas ya dadas apuntan al código del nivel (<span className="mono">L0</span>,
              <span className="mono"> L1</span>, <span className="mono">L2</span>…), no a su etiqueta. Si borras un nivel
              o le cambias el código, las respuestas que apuntaban a él se pierden; cambiar solo la etiqueta o la
              descripción no afecta a lo ya valorado.
            </div>
          </div>
        )}

        {blocking && (
          <div className="banner warn">
            <Icon name="alert" size={18} className="banner-icon" />
            <div className="tiny" style={{ flex: 1 }}>{blocking}</div>
          </div>
        )}
      </div>
    </Modal>
    <Modal
      open={confirmandoBorrado}
      onClose={() => setConfirmandoBorrado(false)}
      title="Eliminar factor"
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={() => setConfirmandoBorrado(false)}>Cancelar</Button>
          <Button variant="primary" icon="trash" onClick={onDelete}>Eliminar</Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 10 }}>
        <div>¿Eliminar el factor «{item.name}»?</div>
        <div className="small muted">
          {respuestasDelFactor > 0
            ? `Se borrarán sus respuestas en ${respuestasDelFactor} ${respuestasDelFactor === 1 ? "puesto" : "puestos"}.`
            : "Ningún puesto lo tiene valorado."}
        </div>
      </div>
    </Modal>
    </>
  );
}

/* ---- Reparto por etiqueta de género ---- */

function GenderBalanceCard({ balance, onEditItem }) {
  const segments = [
    { id: "NEUTRO",    label: "Sin etiqueta", value: balance.neutral },
    { id: "MASCULINO", label: "Masculino",    value: balance.masculine },
    { id: "FEMENINO",  label: "Femenino",     value: balance.feminine },
  ];

  return (
    <Card
      eyebrow="Control de sesgo"
      title="Reparto por etiqueta de género"
      action={<Badge tone={balance.gendered > 0 ? "warn" : "good"} dot>{balance.items.length} etiquetados</Badge>}
    >
      <div className="grid" style={{ gridTemplateColumns: "300px 1fr", gap: 20, alignItems: "start" }}>
        <div className="stack" style={{ gap: 12 }}>
          <div>
            <div className="display-num" style={{ fontSize: 40, lineHeight: 1 }}>
              {fmtPct(balance.gendered)}<span style={{ fontSize: 22 }}>%</span>
            </div>
            <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.5 }}>
              del peso final de la valoración recae en subfactores con etiqueta de género.
            </div>
          </div>

          <div style={{ display: "flex", height: 14, borderRadius: 999, overflow: "hidden", background: "var(--ink-150)" }}>
            {segments.filter(s => s.value > 0).map(s => (
              <div key={s.id}
                title={`${s.label}: ${fmtPct(s.value)}%`}
                style={{ width: s.value + "%", background: GENDER_TINT[s.id].bar }} />
            ))}
          </div>

          <div className="stack" style={{ gap: 6 }}>
            {segments.map(s => (
              <div key={s.id} className="spread">
                <div className="row" style={{ gap: 8 }}>
                  <span className="swatch" style={{ background: GENDER_TINT[s.id].bar }} />
                  <span className="tiny">{s.label}</span>
                </div>
                <span className="mono semi" style={{ fontSize: 12 }}>{fmtPct(s.value)}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="stack" style={{ gap: 12, minWidth: 0 }}>
          <div className="banner info">
            <Icon name="info" size={18} className="banner-icon" />
            <div className="tiny" style={{ flex: 1, lineHeight: 1.5 }}>
              La etiqueta de género es <b>informativa y no interviene en el cálculo de puntos</b>. Sirve para ver si la
              valoración se apoya demasiado en factores históricamente asociados a un género (por ejemplo, esfuerzo
              físico frente a esfuerzo emocional) y poder reequilibrarla.
            </div>
          </div>

          {balance.items.length === 0 ? (
            <div className="banner good">
              <Icon name="circle-check" size={18} className="banner-icon" />
              <div className="tiny" style={{ flex: 1, lineHeight: 1.5 }}>
                Ningún subfactor activo lleva etiqueta de género: todo el peso de la valoración recae en subfactores
                neutros.
              </div>
            </div>
          ) : (
            <>
              <table className="t-table">
                <thead><tr>
                  <th>Subfactor</th>
                  <th style={{ width: 130 }}>Grupo</th>
                  <th style={{ width: 110 }}>Etiqueta</th>
                  <th style={{ width: 110 }}>Peso efectivo</th>
                  <th style={{ width: 60 }}></th>
                </tr></thead>
                <tbody>
                  {balance.items.map(it => (
                    <tr key={it.groupId + "/" + it.id}>
                      <td><b>{it.name}</b></td>
                      <td className="muted">{it.groupName}</td>
                      <td><GenderChip gender={it.gender} /></td>
                      <td>
                        <span className="mono semi" style={{ color: GENDER_TINT[it.gender].fg }}>
                          {fmtPct(it.effective)}%
                        </span>
                      </td>
                      <td>
                        <div className="row" style={{ justifyContent: "flex-end" }}>
                          <button className="icon-btn" title="Editar subfactor"
                            onClick={() => onEditItem(it.groupId, it.id)}>
                            <Icon name="edit" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="tiny muted" style={{ lineHeight: 1.5 }}>
                El peso efectivo es la parte de la puntuación total que depende de ese subfactor: el SVPT de su grupo
                por su peso dentro del grupo.
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

/* Pantalla Factores: la rama del método de la empresa, con el selector de
   método arriba en las dos. */
function Factors() {
  const { state } = useStore();
  const selector = <MetodoValoracionCard />;
  return state.metodo === "ministerio"
    ? <FactoresMinisterio selectorMetodo={selector} />
    : <FactorsTalention selectorMetodo={selector} />;
}

export { Factors };
